/**
 * THE avatar colour scheme — one per person, the same on every surface (the taskbar, the drive's
 * access lists, the share dialogs, the PDApp splash and its identity bar, the account pages).
 *
 * A seed (an email, a name, an id) hashes to one of sixteen hues; each hue is a soft 135° gradient
 * into its neighbour. Framework-free so the server-rendered splash can use it; `@papan/core-ui`'s
 * `avatarBg` maps the same index to Tailwind classes, and the two static account pages
 * (apps/web/public/__accounts/{embed,consent}.html) carry a copy pinned by a parity test.
 *
 * SEMANTIC — DO NOT BRAND: sixteen distinct hues so different people never look alike.
 */
export const AVATAR_GRADIENTS: ReadonlyArray<readonly [string, string]> = [
  ['#f87171', '#f43f5e'], // red → rose
  ['#fb923c', '#ef4444'], // orange → red
  ['#fbbf24', '#f97316'], // amber → orange
  ['#a3e635', '#22c55e'], // lime → green
  ['#4ade80', '#10b981'], // green → emerald
  ['#34d399', '#14b8a6'], // emerald → teal
  ['#2dd4bf', '#06b6d4'], // teal → cyan
  ['#22d3ee', '#0ea5e9'], // cyan → sky
  ['#38bdf8', '#3b82f6'], // sky → blue
  ['#60a5fa', '#6366f1'], // blue → indigo
  ['#818cf8', '#c084fc'], // indigo → purple
  ['#a78bfa', '#a855f7'], // violet → purple
  ['#c084fc', '#d946ef'], // purple → fuchsia
  ['#e879f9', '#ec4899'], // fuchsia → pink
  ['#f472b6', '#f43f5e'], // pink → rose
  ['#fb7185', '#ef4444'], // rose → red
]

/** The hue for a seed. Case-folded and trimmed — the same person reaches this through fields that
 *  disagree on case, and one differing letter would pick another colour. */
export function avatarHueIndex(seed: string): number {
  const normalized = (seed || '').trim().toLowerCase()
  let hash = 0
  for (let i = 0; i < normalized.length; i++) hash = (hash * 31 + normalized.charCodeAt(i)) >>> 0
  return hash % AVATAR_GRADIENTS.length
}

/** The CSS background for a seed's avatar. */
export function avatarGradient(seed: string): string {
  return avatarGradientForHue(avatarHueIndex(seed))
}

/** The CSS background for a hue index — what a client draws when the server sent only the hue.
 *  A seed can be an email the viewer must not learn (a Personal maker's), so a server never sends
 *  the seed itself, only `avatarHueIndex(seed)`. Out-of-range ⇒ the first hue. */
export function avatarGradientForHue(hue: number): string {
  const [from, to] = AVATAR_GRADIENTS[Number.isInteger(hue) && hue >= 0 && hue < AVATAR_GRADIENTS.length ? hue : 0]!
  return `linear-gradient(135deg, ${from}, ${to})`
}

/** THE avatar initials, the same on every surface: two letters for two names, one for one
 *  (owner, 2026-11-03 — the common convention). "John Doe" → "JD", "Tap" → "T",
 *  "alice.smith@x" → "AS", "alice@x" → "A". An email's domain is stripped first. A
 *  word that doesn't start with a letter or digit is a qualifier, not a name — "Draco Chu
 *  (dragonwhites)" → "DC", never "D(" (#1037). Masking stars are not letters: a masked "k***@…"
 *  gives the one letter it shows, "K". */
export function avatarInitials(raw: string): string {
  const base = ((raw || '').includes('@') ? raw.split('@')[0]! : (raw || '')).replace(/\*+/g, ' ').trim()
  const words = base.split(/[\s_.\-]+/).filter(Boolean)
  const named = words.filter((w) => /^[\p{L}\p{N}]/u.test(w))
  const parts = named.length > 0 ? named : words
  if (parts.length >= 2) {
    return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase()
  }
  const single = parts[0] || base
  return (Array.from(single)[0] || '?').toUpperCase()
}
